import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import EvidencePanel from "./EvidencePanel";

const MANUAL_OPTIONS = [
  ["pass", "Pass", "The control is implemented as described in the audit steps."],
  ["fail", "Fail", "The control is not implemented - it counts against the score."],
  ["na", "Not applicable", "The workload or feature is not used in this tenant."],
  ["exception", "Accepted risk", "Known gap, formally accepted - excluded from the score with a justification."],
];
const AUTO_OPTIONS = [
  ["exception", "Accepted risk", "Keep the automated Fail on record but exclude it from the score with a justification."],
  ["na", "Not applicable", "The workload or feature is not used in this tenant."],
];

const addDays = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const defaultReview = (status) => addDays(status === "exception" ? 90 : 365);

/** Record a manual attestation or an accepted-risk exception for a control. */
export default function AttestDialog({ tenantId, control, mode, isAdmin = true, onFilesChange, onClose, onSaved }) {
  const options = control.automated ? AUTO_OPTIONS : MANUAL_OPTIONS;
  const [files, setFiles] = useState(control.files || []);
  const initial = control.attestation?.status && options.some(([k]) => k === control.attestation.status) ? control.attestation.status : mode === "exception" ? "exception" : "pass";
  const [status, setStatusRaw] = useState(initial);
  const showEvidence = !control.automated || status === "exception";
  const [note, setNote] = useState(control.attestation?.note || "");
  const [reviewAt, setReviewAt] = useState(control.attestation?.review_at || defaultReview(initial));
  const [reviewTouched, setReviewTouched] = useState(!!control.attestation?.review_at);
  const [busy, setBusy] = useState(false);
  const needsNote = status === "exception";
  const setStatus = (k) => {
    setStatusRaw(k);
    if (!reviewTouched) setReviewAt(defaultReview(k));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authApi.compliance.attest(tenantId, control.id, { status, note, review_at: reviewAt || undefined });
      toast.success(status === "exception" ? "Accepted risk recorded" : "Attestation saved", { description: `Review by ${res.review_at}` });
      onSaved(res);
    } catch (err) {
      toast.error("Could not save", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl tenant-dialog" data-testid="cis-attest-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              <span className="mono">{control.id}</span> · {control.automated ? "Accept risk" : "Attest control"}
            </DialogTitle>
            <DialogDescription>{control.title}</DialogDescription>
          </DialogHeader>
          <div className="ws-form">
            {control.automated && <div className="ws-alert warn">Automated result: {control.evidence}</div>}
            <div className="cis-radio-list" role="radiogroup" aria-label="Status">
              {options.map(([k, label, help]) => (
                <label key={k} className={`radio-card ${status === k ? "on" : ""}`} data-testid={`cis-attest-option-${k}`}>
                  <input type="radio" name="status" value={k} checked={status === k} onChange={() => setStatus(k)} />
                  <span>
                    <b>{label}</b>
                    <small>{help}</small>
                  </span>
                </label>
              ))}
            </div>
            <label className="auth-field">
              <span>{needsNote ? "Justification (required, min. 10 characters)" : "Note (optional)"}</span>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={needsNote ? "Why is this risk acceptable, what compensating controls exist, and when will it be reviewed?" : "Evidence reference, ticket number, reviewer comments..."} data-testid="cis-attest-note" />
            </label>
            <label className="auth-field">
              <span>
                <CalendarClock size={12} /> Review by <small className="muted">(default: accepted risk 90 days, attestation 365 days)</small>
              </span>
              <Input type="date" value={reviewAt} min={addDays(0)} onChange={(e) => (setReviewTouched(true), setReviewAt(e.target.value))} required data-testid="cis-attest-review" />
            </label>
            {showEvidence && (
              <EvidencePanel
                tenantId={tenantId}
                controlId={control.id}
                files={files}
                isAdmin={isAdmin}
                compact
                onChange={(next) => {
                  setFiles(next);
                  onFilesChange?.(control.id, next);
                }}
              />
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || (needsNote && note.trim().length < 10)} data-testid="cis-attest-save">
              {busy && <Loader2 size={14} className="spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
